// ─────────────────────────────────────────────────────────────────────────────
// ProductionSpotlight, the FOH/Monitor/Tech hero. 1:1 port of the next-show
// venue spotlight in production-assets-wiring.js, on CD's spotlight shell. Hero =
// next confirmed show; three stats = FOH console / Stage / Capacity from the
// crowd-sourced venue spec. The 5 spec tiles + crew notes live below in
// <ProdVenueSpecs> (the movers slot).
// ─────────────────────────────────────────────────────────────────────────────

import type { CSSProperties, JSX } from 'react';
import { useProductionSpotlight } from '../../lib';

function spotlightStyle(photoUrl: string): CSSProperties | undefined {
  if (!photoUrl) return undefined;
  const safe = photoUrl.replace(/["'()\\\s]/g, '');
  return {
    backgroundImage:
      'linear-gradient(135deg,rgba(26,14,13,0.92) 0%,rgba(45,21,24,0.78) 45%,rgba(139,26,43,0.55) 100%),' +
      "url('" + safe + "')",
    backgroundSize: 'cover, cover',
    backgroundPosition: 'center, center',
    backgroundRepeat: 'no-repeat, no-repeat',
  };
}

export interface ProductionSpotlightProps {
  readonly photoUrl?: string;
}

export function ProductionSpotlight({ photoUrl = '' }: ProductionSpotlightProps): JSX.Element {
  const { data } = useProductionSpotlight();
  const hasPhoto = !!photoUrl;
  const cardClass = 'spotlight' + (hasPhoto ? ' spotlight--has-photo' : '');

  return (
    <article className={cardClass} id="prod-spotlight-card" style={spotlightStyle(photoUrl)}>
      {!hasPhoto && <div className="spotlight__bg" aria-hidden="true"></div>}

      <header className="spotlight__head">
        <div>
          <p className="spotlight__eyebrow">{data.eyebrow}</p>
          <h2 className="spotlight__title disp">{data.title}</h2>
          <p className="spotlight__sub">{data.sub}</p>
        </div>
        {data.verified && <span className="delta delta--up">★ Verified</span>}
      </header>

      <div className="spotlight__stats">
        <div className="statile">
          <p className="statile__label">FOH console</p>
          <p className="statile__value statile__value--sm">{data.fohConsole}</p>
        </div>
        <div className="statile">
          <p className="statile__label">Stage</p>
          <p className="statile__value statile__value--sm">{data.stage}</p>
        </div>
        <div className="statile">
          <p className="statile__label">Capacity</p>
          <p className="statile__value">{data.capacity}</p>
        </div>
      </div>
    </article>
  );
}
